/**
 * A controlled tooltip component that works with both Link and button elements.
 * When href is provided, it renders as a Next.js Link.
 * When href is not provided, it renders as a button.
 */
import Link from "next/link";
import { Tooltip, TooltipFocusableTrigger } from "@zapier/design-system";
import { ReactNode } from "react";

type BaseProps = {
  key?: string;
  content: string;
  placement?: "top" | "bottom" | "left" | "right";
  offset?: "small" | "medium" | "large";
  className?: string;
  children: ReactNode;
  disabled?: boolean;
};

type LinkProps = BaseProps & {
  href: string;
  target: string;
  onClick?: never;
};

type ButtonProps = BaseProps & {
  href?: never;
  target?: never;
  onClick: () => void;
};

type Props = LinkProps | ButtonProps;

export function ButtonOrLinkWithTooltip({
  key,
  content,
  placement = "top",
  offset = "large",
  className,
  onClick,
  href,
  target,
  children,
  disabled,
}: Props) {
  const getTag = (href: Props["href"]) => {
    if (href) {
      return Link;
    }
    return "button";
  };

  const Component = getTag(href) as typeof Link | "button";

  return (
    <Tooltip key={key} content={content} placement={placement} offset={offset}>
      <TooltipFocusableTrigger>
        {/* @ts-ignore - Component is either Link or button based on href prop */}
        <Component
          aria-label={content}
          className={className}
          onClick={onClick}
          {...(href ? { href, target } : {})}
          {...(disabled ? { disabled } : {})}
        >
          {children}
        </Component>
      </TooltipFocusableTrigger>
    </Tooltip>
  );
}
